import z from "zod";
import type { EscrowStatus } from "@remi/enums/escrow-status";
import type { HistoryOrigin } from "@remi/enums/history-origin";
import type { ProjectFinancialEventType } from "@remi/enums/project-financial-event-type";
import type { RemiEntity } from "@remi/enums/remi-entity";
import type { VendorCreditType } from "@remi/enums/vendor-credit-type";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const accountingErrorTypeValues = [
  "UNKNOWN",
  "FINANCIAL_EVENT_BREAKDOWN_INVALID",
  "FINANCIAL_EVENT_ALREADY_TOMBSTONED",
  "FINANCIAL_EVENT_REF_SCOPE_INVALID",
  "BILL_FINANCIAL_EVENT_CAPTURE_FAILED",
  "CREDIT_FINANCIAL_EVENT_CAPTURE_FAILED",
  "WORK_ORDER_FINANCIAL_EVENT_CAPTURE_FAILED",
  "VENDOR_CREDIT_DELETE_BOOKS_CLOSED",
  "REMITTANCE_NOTE_UPDATE_UNAUTHORIZED",
  "CUSTOMER_ID_NOT_FOUND",
  "ESCROW_COMPANY_CUSTOMER_MISSING",
  "FINANCE_COMPANY_CUSTOMER_MISSING",
  "ACCOUNT_IDS_MISSING",
  "JOURNAL_ENTRY_DATE_NOT_FOUND",
  "JOURNAL_ENTRY_TXN_DATE_NOT_FOUND",
  "CUSTOMER_LOOKUP_FAILED",
  "PARTNER_NOT_FOUND",
  "INSUFFICIENT_JOURNAL_ENTRY_LINES",
  "ACCOUNTING_CUSTOMER_NOT_FOUND",
  "INVOICE_BUCKET_NOT_FOUND",
  "NO_PROJECTS_FOR_INVOICE_PDF",
  "CUSTOMER_NAME_REQUIRED",
  "CUSTOMER_ADDRESS_REQUIRED",
  "PROJECT_ID_NOT_FOUND",
  "INVOICE_PROJECT_NOT_FOUND",
  "INVOICE_EXTERNAL_ID_MISSING",
  "CUSTOMER_NOT_FOUND_FOR_BILL",
  "JOURNAL_ENTRY_TYPE_NOT_FOUND",
  "JOURNAL_ENTRY_DELETE_BOOKS_CLOSED",
  "JOURNAL_ENTRY_NOT_FOUND",
  "PAYEE_ID_NOT_FOUND",
  "INVALID_ADJUSTER_ACCOUNT",
  "PROJECT_NOT_FOUND",
  "VENDOR_ID_NOT_FOUND",
  "SOLAR_ORGANIZATION_NOT_FOUND",
  "SOLAR_PARENT_CUSTOMER_NOT_FOUND",
  "ESCROW_PARENT_CUSTOMER_NOT_FOUND",
  "FINANCE_PARENT_CUSTOMER_NOT_FOUND",
  "CREDIT_MEMO_DELETE_BOOKS_CLOSED",
  "WORK_ORDER_AMOUNT_NOT_FOUND",
  "CONTRACTOR_NOT_FOUND",
  "BILL_DELETE_BOOKS_CLOSED",
  "BILL_ALREADY_PAID",
  "BILL_DATE_NOT_FOUND",
  "BILL_NOT_FOUND",
  "VENDOR_NOT_FOUND_FOR_BILL",
  "QUICKBOOKS_VENDOR_NOT_FOUND",
  "QUICKBOOKS_ENTITY_NOT_FOUND",
  "QUICKBOOKS_QUERY_FAILED",
  "QUICKBOOKS_CDC_DELETION_FAILED",
  "RFQ_PROJECTS_ERROR",
  "PARTNER_CUSTOMER_NOT_FOUND",
  "INVOICE_VOID_FAILED",
  "INVOICE_CREATION_FAILED",
  "INVOICE_CLOSED_CANNOT_VOID",
  "INVOICE_NOT_FOUND",
  "ACTIVE_QUOTE_NOT_FOUND",
  "ESCROW_MISSING_QUOTE",
  "ESCROW_SUB_DOLLAR_RESIDUAL_ON_FUNDED_ESCROW",
  "ESCROW_RELEASED_FUNDS_EXCEED_QUOTE",
  "ESCROW_ALREADY_TERMINAL",
  "ESCROW_CONTRACT_BELOW_COMMITTED_ESCROW",
  "ESCROW_UPSERT_LOCK_CONTENDED",
  "ESCROW_CANCELLED_WITHOUT_LOCAL_INTENT",
  "FINANCING_MISSING_HIC_FILE",
  "FINANCING_MISSING_REQUEST_FOR_QUOTE",
  "FINANCING_MISSING_ACTIVE_QUOTE_FOR_MILESTONE",
  "FINANCING_MISSING_LOAN_AMOUNT",
  "FINANCING_MISSING_PROJECT_COST",
  "FUNDING_NO_REQUESTS_FOR_QUOTE",
  "FUNDING_NO_BID_FOUND",
  "FUNDING_NO_MILESTONE_FOR_CATEGORY",
  "FUNDING_MISSING_ACTIVE_QUOTE",
  "FUNDING_DUPLICATE_CANCELLATION_MILESTONE",
  "FUNDING_EXISTING_CANCELLATION_FEE",
  "FUNDING_MISSING_ACTIVE_QUOTE_FOR_CANCELLATION",
  "FUNDING_CANCELLATION_FEE_EXCEEDS_FINANCING",
  "FINANCING_EXTERNAL_ID_NOT_FOUND",
  "FINANCING_REQUEST_FOR_QUOTE_NOT_FOUND",
  "FINANCING_EXTERNAL_ID_ALREADY_EXISTS",
  "FINANCING_CREATION_FAILED",
  "FINANCING_CLAIM_RECOVERY_FAILED",
  "FINANCING_NOT_FOUND",
  "FINANCING_INVALID_STATUS_FOR_UPDATE",
  "FINANCING_INVALID_STATUS_FOR_ZERO_AMOUNT_CANCEL",
  "INVALID_STIPULATION_DOCUMENT_TYPE",
  "FINANCING_STIPULATIONS_MALFORMED",
  "STIPULATION_UPLOAD_FAILED",
  "CALL_STIPULATIONS_NOT_APPROVED",
  "CASH_CHECK_ALREADY_EXISTS",
  "CASH_CHECK_CUSTOMER_NOT_FOUND",
  "CASH_CHECK_NOT_FOUND",
  "ESCROW_NOT_FOUND",
  "INVOICE_REMI_ENTITY_MISMATCH",
  "INVOICE_REMI_ID_NOT_FOUND",
  "INVOICE_VOID_BATCH_ERROR",
  "CREDIT_MEMO_DELETE_BATCH_ERROR",
  "QB_INVOICE_FETCH_BATCH_ERROR",
  "QB_CREDIT_MEMO_FETCH_BATCH_ERROR",
  "JOURNAL_ENTRY_PROJECT_ID_NOT_FOUND",
  "CREDIT_MEMO_REMI_ID_NOT_FOUND",
  "VENDOR_CREDIT_REMI_ID_NOT_FOUND",
  "VENDOR_CREDIT_VENDOR_NOT_FOUND",
  "VENDOR_CREDIT_PAYEE_NOT_IN_QUICKBOOKS",
  "VENDOR_CREDIT_SUPPLIER_PAYEE_REQUIRES_MATERIALS",
  "FINANCING_SUBMISSION_ERROR",
  "BILL_REMI_ID_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "INVALID_SELECTED_NET_TERMS",
  "MISMATCHED_BIDS_FOR_MOVE",
  "INVOICE_BID_REQUEST_ID_NOT_FOUND",
  "BID_REQUEST_EXTERNAL_ID_NOT_FOUND",
  "ESCROW_NO_FUNDS_AVAILABLE",
  "PAYKEEPER_SCHEDULE_READ_FAILED",
  "CONTRACT_PARTY_NOT_FOUND",
  "NEGATIVE_INVOICE_PAYLOAD",
  "ORPHANED_CUSTOMER_OVER_CREDITED",
  "BID_NOT_FOUND_FOR_QB_ENTITY",
  "ESCROW_HOMEOWNER_MISSING_CONTACT",
  "ESCROW_CONTRACTOR_ORG_MISSING",
  "FUNDING_USER_MISSING_EMAIL",
  "STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE",
  "FINANCING_QUOTE_AREA_ZERO",
  "JOURNAL_ENTRY_OPERATION_IN_PROGRESS",
  "BILLS_PENDING_REVIEW",
  "WORK_ORDER_ONLY_INVOICE_REPORT_UNREADABLE_SUBMISSION",
  "WORK_ORDER_ONLY_INVOICE_REPORT_WORK_ORDER_NUMBER_INVALID",
  "WORK_ORDER_ONLY_INVOICE_REPORT_WINDOW_INCOMPLETE",
  "WORK_ORDER_ONLY_INVOICE_REPORT_SEND_UNCONFIRMED",
  "COC_INVOICE_SOURCE_NOT_FOUND",
  "COC_INVOICE_MULTIPLE_ACTIVE_QUOTES",
  "COC_INVOICE_ESTIMATE_MISSING",
  "COC_INVOICE_AMOUNT_INVALID",
  "COC_INVOICE_BILL_TO_MISSING",
  "COC_INVOICE_VERSION_CONFLICT",
] as const;
const accountingErrorTypeSchema = z.enum(accountingErrorTypeValues);
const AccountingErrorType = accountingErrorTypeSchema.enum;
type AccountingErrorType = z.infer<typeof accountingErrorTypeSchema>;

export type AccountingErrors =
  | { errorType: typeof AccountingErrorType.UNKNOWN; data: { test: string } }
  | {
      errorType: typeof AccountingErrorType.FINANCIAL_EVENT_BREAKDOWN_INVALID;
      data: { projectId: number; issues: string } | { eventId: string; issues: string };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCIAL_EVENT_ALREADY_TOMBSTONED;
      data: { eventId: string };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCIAL_EVENT_REF_SCOPE_INVALID;
      data: { projectId: number; eventType: ProjectFinancialEventType; scope: string; refType: string };
    }
  | {
      errorType: typeof AccountingErrorType.BILL_FINANCIAL_EVENT_CAPTURE_FAILED;
      data: { billId: string; projectId: number; eventType: ProjectFinancialEventType; cause: string };
    }
  | {
      errorType: typeof AccountingErrorType.CREDIT_FINANCIAL_EVENT_CAPTURE_FAILED;
      data: { entityId: string; projectId: number; eventType: ProjectFinancialEventType; cause: string };
    }
  | {
      errorType: typeof AccountingErrorType.WORK_ORDER_FINANCIAL_EVENT_CAPTURE_FAILED;
      data: { projectId: number; eventType: ProjectFinancialEventType; cause: string };
    }
  | {
      errorType: typeof AccountingErrorType.VENDOR_CREDIT_DELETE_BOOKS_CLOSED;
      data: { vendorCreditId: string; accountingBooksClosedAt: string };
    }
  | {
      errorType: typeof AccountingErrorType.REMITTANCE_NOTE_UPDATE_UNAUTHORIZED;
      data: { kind: string; id: string; field: string };
    }
  | { errorType: typeof AccountingErrorType.CUSTOMER_ID_NOT_FOUND; data: { projectId?: number; bidId?: string } }
  | {
      errorType: typeof AccountingErrorType.ESCROW_COMPANY_CUSTOMER_MISSING;
      data: { projectId?: number; bidId?: string };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCE_COMPANY_CUSTOMER_MISSING;
      data: { projectId?: number; bidId?: string };
    }
  | {
      errorType: typeof AccountingErrorType.ACCOUNT_IDS_MISSING;
      data: {
        projectId: number;
        unbilledArAccountId?: string;
        revenueAccountId?: string;
        tpaExpenseAccountId?: string;
        tpaCreditAccountId?: string;
      };
    }
  | { errorType: typeof AccountingErrorType.JOURNAL_ENTRY_DATE_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof AccountingErrorType.JOURNAL_ENTRY_TXN_DATE_NOT_FOUND;
      data: { externalId: string; remiId?: string | null; projectId?: number };
    }
  | { errorType: typeof AccountingErrorType.CUSTOMER_LOOKUP_FAILED; data: { projectId: number; error: string } }
  | { errorType: typeof AccountingErrorType.PARTNER_NOT_FOUND; data: { projectId?: number; bidId?: string } }
  | {
      errorType: typeof AccountingErrorType.INSUFFICIENT_JOURNAL_ENTRY_LINES;
      data: { projectId?: number; bidId?: string };
    }
  | {
      errorType: typeof AccountingErrorType.ACCOUNTING_CUSTOMER_NOT_FOUND;
      data: { customerId?: string; remiEntity?: string; projectId: number };
    }
  | { errorType: typeof AccountingErrorType.INVOICE_BUCKET_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.NO_PROJECTS_FOR_INVOICE_PDF; data: { partyName: string } }
  | { errorType: typeof AccountingErrorType.CUSTOMER_NAME_REQUIRED; data: { partyName: string; projectIds: number[] } }
  | {
      errorType: typeof AccountingErrorType.CUSTOMER_ADDRESS_REQUIRED;
      data: { partyName: string; projectIds: number[] };
    }
  | { errorType: typeof AccountingErrorType.PROJECT_ID_NOT_FOUND; data: { partyName: string } }
  | {
      errorType: typeof AccountingErrorType.INVOICE_PROJECT_NOT_FOUND;
      data: { projectId: number; externalId: string; remiEntity: RemiEntity | string; docNumber?: string };
    }
  | { errorType: typeof AccountingErrorType.INVOICE_EXTERNAL_ID_MISSING; data: { invoiceId: string } }
  | { errorType: typeof AccountingErrorType.CUSTOMER_NOT_FOUND_FOR_BILL; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.JOURNAL_ENTRY_TYPE_NOT_FOUND; data: { billType: string } }
  | {
      errorType: typeof AccountingErrorType.JOURNAL_ENTRY_DELETE_BOOKS_CLOSED;
      data: { journalEntryId: number; accountingBooksClosedAt: string };
    }
  | { errorType: typeof AccountingErrorType.JOURNAL_ENTRY_NOT_FOUND; data: { journalEntryId: string } }
  | { errorType: typeof AccountingErrorType.PAYEE_ID_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.INVALID_ADJUSTER_ACCOUNT; data: { account: string; amount: number } }
  | { errorType: typeof AccountingErrorType.PROJECT_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.VENDOR_ID_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.SOLAR_ORGANIZATION_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.SOLAR_PARENT_CUSTOMER_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.ESCROW_PARENT_CUSTOMER_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.FINANCE_PARENT_CUSTOMER_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof AccountingErrorType.CREDIT_MEMO_DELETE_BOOKS_CLOSED;
      data: { creditMemoId: string; accountingBooksClosedAt: string };
    }
  | { errorType: typeof AccountingErrorType.WORK_ORDER_AMOUNT_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.CONTRACTOR_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof AccountingErrorType.BILL_DELETE_BOOKS_CLOSED;
      data: { billId: string; accountingBooksClosedAt: string };
    }
  | { errorType: typeof AccountingErrorType.BILL_ALREADY_PAID; data: { billId: string } }
  | { errorType: typeof AccountingErrorType.BILL_DATE_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.BILL_NOT_FOUND; data: { billId: string } }
  | {
      errorType: typeof AccountingErrorType.VENDOR_NOT_FOUND_FOR_BILL;
      data: {
        projectId: number;
        remiEntity: RemiEntity;
        payeeId: string;
        billExternalId?: string;
        vendorExternalId?: string;
        vendorName?: string | null;
      };
    }
  | { errorType: typeof AccountingErrorType.QUICKBOOKS_VENDOR_NOT_FOUND; data: { contractorId: string } }
  | {
      errorType: typeof AccountingErrorType.QUICKBOOKS_ENTITY_NOT_FOUND;
      data: { txnId: string; entityType: string };
    }
  | {
      errorType: typeof AccountingErrorType.QUICKBOOKS_QUERY_FAILED;
      data: { txnId: string; entityType: string; remiEntity: string; error: string };
    }
  | {
      errorType: typeof AccountingErrorType.QUICKBOOKS_CDC_DELETION_FAILED;
      data: { entityName: string; externalId: string; remiEntity: RemiEntity | string };
    }
  | { errorType: typeof AccountingErrorType.RFQ_PROJECTS_ERROR; data: { requestForQuoteId: number } }
  | { errorType: typeof AccountingErrorType.PARTNER_CUSTOMER_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AccountingErrorType.INVOICE_VOID_FAILED; data: { invoiceId: string } }
  | { errorType: typeof AccountingErrorType.INVOICE_CREATION_FAILED; data: { customerId: string; projectId: number } }
  | { errorType: typeof AccountingErrorType.INVOICE_CLOSED_CANNOT_VOID; data: { invoiceId: string } }
  | { errorType: typeof AccountingErrorType.ESCROW_MISSING_QUOTE; data: { requestForQuoteId: number } }
  | {
      errorType: typeof AccountingErrorType.ESCROW_SUB_DOLLAR_RESIDUAL_ON_FUNDED_ESCROW;
      data: {
        requestForQuoteId: number;
        bidId: number;
        escrowExternalId: string;
        escrowAmount: number;
        fundedAmount: number;
        pendingAmount: number;
        disbursedAmount: number;
      };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_ALREADY_TERMINAL;
      data: { requestForQuoteId: number; escrowId: string; status: string };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_CONTRACT_BELOW_COMMITTED_ESCROW;
      data: {
        requestForQuoteId: number;
        bidId: number;
        committedAmount: number;
        shortfall: number;
      };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_RELEASED_FUNDS_EXCEED_QUOTE;
      data: {
        requestForQuoteId: number;
        bidId: number;
        quoteVersionId: string;
        submittedAmount: number;
        escrowExternalId: string;
        fundedAmount: number;
        pendingAmount: number;
        disbursedAmount: number;
      };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_UPSERT_LOCK_CONTENDED;
      data: { requestForQuoteId: number; waitedMs: number };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_CANCELLED_WITHOUT_LOCAL_INTENT;
      data: { escrowExternalId: string; bidId: number };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_MISSING_HIC_FILE;
      data: { pandadocDocumentId: string; bidId: number };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_MISSING_REQUEST_FOR_QUOTE;
      data: { pandadocDocumentId: string };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_MISSING_ACTIVE_QUOTE_FOR_MILESTONE;
      data: { requestForQuoteId: number };
    }
  | { errorType: typeof AccountingErrorType.FINANCING_MISSING_LOAN_AMOUNT; data: { financingId: string } }
  | { errorType: typeof AccountingErrorType.FINANCING_MISSING_PROJECT_COST; data: { requestForQuoteId: number } }
  | { errorType: typeof AccountingErrorType.FUNDING_NO_REQUESTS_FOR_QUOTE; data: { bidId: number } }
  | { errorType: typeof AccountingErrorType.FUNDING_NO_BID_FOUND; data: { bidId: number } }
  | {
      errorType: typeof AccountingErrorType.FUNDING_NO_MILESTONE_FOR_CATEGORY;
      data: { projectId: number; projectCategory: string };
    }
  | { errorType: typeof AccountingErrorType.FUNDING_MISSING_ACTIVE_QUOTE; data: { requestForQuoteId: number } }
  | {
      errorType: typeof AccountingErrorType.FUNDING_DUPLICATE_CANCELLATION_MILESTONE;
      data: { requestForQuoteId: number };
    }
  | { errorType: typeof AccountingErrorType.FUNDING_EXISTING_CANCELLATION_FEE; data: { requestForQuoteId: number } }
  | {
      errorType: typeof AccountingErrorType.FUNDING_MISSING_ACTIVE_QUOTE_FOR_CANCELLATION;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof AccountingErrorType.FUNDING_CANCELLATION_FEE_EXCEEDS_FINANCING;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_EXTERNAL_ID_NOT_FOUND;
      data: { requestForQuoteId: number } | { externalId: string };
    }
  | { errorType: typeof AccountingErrorType.FINANCING_REQUEST_FOR_QUOTE_NOT_FOUND; data: { requestForQuoteId: number } }
  | {
      errorType: typeof AccountingErrorType.FINANCING_EXTERNAL_ID_ALREADY_EXISTS;
      data: { externalId: string; vendor: string; requestForQuoteId: number };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_CREATION_FAILED;
      data: { externalId: string; requestForQuoteId: number };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_CLAIM_RECOVERY_FAILED;
      data: { requestForQuoteId: number; vendor: string };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_NOT_FOUND;
      data: { financingId?: string; externalId?: string; vendor?: string; requestForQuoteId?: number };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_INVALID_STATUS_FOR_UPDATE;
      data: {
        requestForQuoteId: number;
        financingId: string;
        vendor: string;
        externalId: string | null;
        status: string;
        requestedLoanAmount: number;
        requestedApr: number;
        requestedTermMonths: number;
      };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_INVALID_STATUS_FOR_ZERO_AMOUNT_CANCEL;
      data: {
        requestForQuoteId: number;
        financingId: string;
        vendor: string;
        externalId: string | null;
        status: string;
        previousLoanAmount: number;
      };
    }
  | {
      errorType: typeof AccountingErrorType.INVALID_STIPULATION_DOCUMENT_TYPE;
      data: { documentType: string; validFolderNames: string[] };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_STIPULATIONS_MALFORMED;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof AccountingErrorType.STIPULATION_UPLOAD_FAILED;
      data: { requestForQuoteId: number; failureCount: number; successCount: number };
    }
  | {
      errorType: typeof AccountingErrorType.CALL_STIPULATIONS_NOT_APPROVED;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof AccountingErrorType.CASH_CHECK_ALREADY_EXISTS;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof AccountingErrorType.CASH_CHECK_CUSTOMER_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.CASH_CHECK_NOT_FOUND;
      data: { id?: string; requestForQuoteId?: number };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_NOT_FOUND;
      data: {
        requestForQuoteId?: number;
        externalId?: string;
        escrowId?: string;
        payKeeperProjectId?: string;
        payKeeperProjectUrl?: string;
        origin?: HistoryOrigin;
        calculatedEscrowStatus?: EscrowStatus;
        contractAmount?: number;
        fundedAmount?: number;
        pendingAmount?: number;
        disbursedAmount?: number;
        snapshotObservedAt?: string;
      };
    }
  | { errorType: typeof AccountingErrorType.INVOICE_REMI_ENTITY_MISMATCH; data: { projectId: number } }
  | {
      errorType: typeof AccountingErrorType.INVOICE_REMI_ID_NOT_FOUND;
      data: { externalId: string; remiEntity: RemiEntity | string };
    }
  | { errorType: typeof AccountingErrorType.INVOICE_VOID_BATCH_ERROR; data: { projectId?: number; errors: unknown[] } }
  | {
      errorType: typeof AccountingErrorType.CREDIT_MEMO_DELETE_BATCH_ERROR;
      data: { projectId?: number; errors: unknown[] };
    }
  | {
      errorType: typeof AccountingErrorType.QB_INVOICE_FETCH_BATCH_ERROR;
      data: { projectId?: number; errors: unknown[] };
    }
  | {
      errorType: typeof AccountingErrorType.QB_CREDIT_MEMO_FETCH_BATCH_ERROR;
      data: { projectId?: number; errors: unknown[] };
    }
  | {
      errorType: typeof AccountingErrorType.JOURNAL_ENTRY_PROJECT_ID_NOT_FOUND;
      data: { externalId: string; remiEntity: RemiEntity; docNumber?: string };
    }
  | {
      errorType: typeof AccountingErrorType.CREDIT_MEMO_REMI_ID_NOT_FOUND;
      data: { externalId: string; remiEntity: RemiEntity; docNumber?: string };
    }
  | {
      errorType: typeof AccountingErrorType.VENDOR_CREDIT_REMI_ID_NOT_FOUND;
      data: { externalId: string; remiEntity: RemiEntity; docNumber?: string };
    }
  | { errorType: typeof AccountingErrorType.VENDOR_CREDIT_VENDOR_NOT_FOUND; data: { externalId: string } }
  | {
      errorType: typeof AccountingErrorType.VENDOR_CREDIT_PAYEE_NOT_IN_QUICKBOOKS;
      data: { projectId: number; payeeId: string; remiEntity: RemiEntity };
    }
  | {
      errorType: typeof AccountingErrorType.VENDOR_CREDIT_SUPPLIER_PAYEE_REQUIRES_MATERIALS;
      data: { projectId: number; payeeId: string; type: VendorCreditType };
    }
  | { errorType: typeof AccountingErrorType.FINANCING_SUBMISSION_ERROR; data: { requestForQuoteId: number } }
  | {
      errorType: typeof AccountingErrorType.BILL_REMI_ID_NOT_FOUND;
      data: { externalId: string; remiEntity: RemiEntity };
    }
  | {
      errorType: typeof AccountingErrorType.REQUEST_FOR_QUOTE_NOT_FOUND;
      data: {
        requestForQuoteId?: number;
        bidProjectId?: number;
        projectId?: number;
        reason?: "bid-id-mismatch";
      };
    }
  | {
      errorType: typeof AccountingErrorType.INVOICE_NOT_FOUND;
      data: { invoiceId: string };
    }
  | {
      errorType: typeof AccountingErrorType.ACTIVE_QUOTE_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.INVALID_SELECTED_NET_TERMS;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.MISMATCHED_BIDS_FOR_MOVE;
      data: { sourceProjectId: number; targetProjectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.INVOICE_BID_REQUEST_ID_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.BID_REQUEST_EXTERNAL_ID_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_NO_FUNDS_AVAILABLE;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.PAYKEEPER_SCHEDULE_READ_FAILED;
      data: { bidId: number; escrowExternalId: string };
    }
  | {
      errorType: typeof AccountingErrorType.CONTRACT_PARTY_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.NEGATIVE_INVOICE_PAYLOAD;
      data: { projectId: number; customerId: string; payloadTotalAmount: number };
    }
  | {
      errorType: typeof AccountingErrorType.ORPHANED_CUSTOMER_OVER_CREDITED;
      data: { projectId: number; customerId: string; invoiceCents: number; creditMemoCents: number; netCents: number };
    }
  | {
      errorType: typeof AccountingErrorType.BID_NOT_FOUND_FOR_QB_ENTITY;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_HOMEOWNER_MISSING_CONTACT;
      data: { bidId: number; hasEmail: boolean; hasPhone: boolean };
    }
  | {
      errorType: typeof AccountingErrorType.ESCROW_CONTRACTOR_ORG_MISSING;
      data: { escrowId: string; bidProjectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.FUNDING_USER_MISSING_EMAIL;
      data: {
        bidId: number;
        requestForQuoteId: number;
        operation: "cancelEscrow" | "flagEscrow" | "getEscrowStatus" | "initFileUploads" | "claimMilestone";
      };
    }
  | {
      errorType: typeof AccountingErrorType.STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE;
      data: {
        reason: "missing" | "unexpected-fields" | "calculator-missing-payment";
        availableProducts?: { term: number; apr: number; productType: string; isACH: boolean }[];
        candidate?: {
          term: number;
          apr: number;
          productType: string;
          isACH: boolean;
          dealerFee?: number;
          promoAPR?: number;
          promoTerm?: number;
        };
      };
    }
  | {
      errorType: typeof AccountingErrorType.FINANCING_QUOTE_AREA_ZERO;
      data: { requestForQuoteId: number; bidId: number; pandadocDocumentId: string };
    }
  | {
      errorType: typeof AccountingErrorType.JOURNAL_ENTRY_OPERATION_IN_PROGRESS;
      data: { projectId: number };
    }
  | {
      errorType: typeof AccountingErrorType.BILLS_PENDING_REVIEW;
      data: { projectId: number; pendingReviewCount: number };
    }
  | {
      errorType: typeof AccountingErrorType.WORK_ORDER_ONLY_INVOICE_REPORT_UNREADABLE_SUBMISSION;
      data: { automationId: string };
    }
  | {
      errorType: typeof AccountingErrorType.WORK_ORDER_ONLY_INVOICE_REPORT_WORK_ORDER_NUMBER_INVALID;
      data: { workOrderExternalId: string };
    }
  | {
      errorType: typeof AccountingErrorType.WORK_ORDER_ONLY_INVOICE_REPORT_WINDOW_INCOMPLETE;
      data: { unaccountedCount: number; rowCount: number; windowStart: string; windowEnd: string };
    }
  | {
      errorType: typeof AccountingErrorType.WORK_ORDER_ONLY_INVOICE_REPORT_SEND_UNCONFIRMED;
      data: { sendingThrough: string; sentThrough: string | null };
    }
  | {
      errorType: typeof AccountingErrorType.COC_INVOICE_SOURCE_NOT_FOUND;
      data: { bidId: number; organizationId: string };
    }
  | {
      errorType: typeof AccountingErrorType.COC_INVOICE_MULTIPLE_ACTIVE_QUOTES;
      data: { bidId: number; organizationId: string; activeQuoteVersionCount: number };
    }
  | {
      errorType: typeof AccountingErrorType.COC_INVOICE_ESTIMATE_MISSING;
      data: { bidId: number; quoteVersionId: string };
    }
  | {
      errorType: typeof AccountingErrorType.COC_INVOICE_AMOUNT_INVALID;
      data: {
        bidId: number;
        organizationId: string;
        quoteVersionId: string;
        totalGSVCents: number;
        deductibleCents: number;
      };
    }
  | {
      errorType: typeof AccountingErrorType.COC_INVOICE_BILL_TO_MISSING;
      data: { bidId: number; partnerFlow: string };
    }
  | {
      errorType: typeof AccountingErrorType.COC_INVOICE_VERSION_CONFLICT;
      data: { bidId: number; organizationId: string; version: number };
    };

export const accountingErrorDetails: Record<AccountingErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Testing unknown error type" },
  FINANCIAL_EVENT_BREAKDOWN_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Financial event breakdown failed write-time validation",
  },
  FINANCIAL_EVENT_ALREADY_TOMBSTONED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Financial event was already tombstoned when a void/correct tried to claim it",
  },
  FINANCIAL_EVENT_REF_SCOPE_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A financial-event ref was passed with a multi-target capture scope",
  },
  BILL_FINANCIAL_EVENT_CAPTURE_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription:
      "Best-effort bill financial-event capture failed after the QuickBooks-interleaved write committed",
  },
  CREDIT_FINANCIAL_EVENT_CAPTURE_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription:
      "Best-effort vendor-credit/credit-memo financial-event capture failed after the QuickBooks-interleaved write committed",
  },
  WORK_ORDER_FINANCIAL_EVENT_CAPTURE_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Work-order financial-event capture failed; the work-order write was rolled back with it",
  },
  VENDOR_CREDIT_DELETE_BOOKS_CLOSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot delete vendor credit because accounting books are closed",
  },
  REMITTANCE_NOTE_UPDATE_UNAUTHORIZED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Caller lacks remittance.update for a remittance note write",
  },
  CUSTOMER_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Customer ID not found",
  },
  ESCROW_COMPANY_CUSTOMER_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Escrow company customer ID is missing",
  },
  FINANCE_COMPANY_CUSTOMER_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Finance company customer ID is missing",
  },
  ACCOUNT_IDS_MISSING: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Missing required account IDs for project",
  },
  JOURNAL_ENTRY_DATE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No journal entry date found for project",
  },
  JOURNAL_ENTRY_TXN_DATE_NOT_FOUND: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "QuickBooks journal entry is missing a transaction date",
  },
  CUSTOMER_LOOKUP_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Customer lookup failed for project",
  },
  PARTNER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No partner found for partner responsible amounts",
  },
  INSUFFICIENT_JOURNAL_ENTRY_LINES: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Journal entry must have at least 2 line items",
  },
  ACCOUNTING_CUSTOMER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Accounting customer not found",
  },
  INVOICE_BUCKET_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No invoice bucket found for project",
  },
  NO_PROJECTS_FOR_INVOICE_PDF: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No projects found for invoice PDF generation",
  },
  CUSTOMER_NAME_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Customer name is required for invoice PDF generation",
  },
  CUSTOMER_ADDRESS_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Customer address is required for invoice PDF generation",
  },
  PROJECT_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No project ID found for invoice PDF generation",
  },
  INVOICE_PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "QuickBooks invoice references missing project",
  },
  INVOICE_EXTERNAL_ID_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invoice has no external ID",
  },
  CUSTOMER_NOT_FOUND_FOR_BILL: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Could not find customer for bill",
  },
  JOURNAL_ENTRY_TYPE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Journal entry type not found for bill type",
  },
  JOURNAL_ENTRY_DELETE_BOOKS_CLOSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot delete journal entry because accounting books are closed",
  },
  JOURNAL_ENTRY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Journal entry not found",
  },
  PAYEE_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No payee ID found for adjuster",
  },
  INVALID_ADJUSTER_ACCOUNT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid adjuster account or amount is zero",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No project found for project ID",
  },
  VENDOR_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No vendor ID found when creating vendor credit",
  },
  SOLAR_ORGANIZATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No solar organization found for project",
  },
  SOLAR_PARENT_CUSTOMER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No solar parent customer found for project",
  },
  ESCROW_PARENT_CUSTOMER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No escrow parent customer found for project",
  },
  FINANCE_PARENT_CUSTOMER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No finance parent customer found for project",
  },
  CREDIT_MEMO_DELETE_BOOKS_CLOSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot delete credit memo because accounting books are closed",
  },
  WORK_ORDER_AMOUNT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Work order amount not found for project",
  },
  CONTRACTOR_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor not found for project",
  },
  BILL_DELETE_BOOKS_CLOSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot delete bill because accounting books are closed",
  },
  BILL_ALREADY_PAID: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot delete bill because it has been paid",
  },
  BILL_DATE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Could not find bill date for project",
  },
  BILL_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bill not found" },
  VENDOR_NOT_FOUND_FOR_BILL: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Could not find vendor for bill",
  },
  QUICKBOOKS_VENDOR_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No vendor found for contractor in QuickBooks",
  },
  QUICKBOOKS_ENTITY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "QuickBooks entity not found with given transaction ID",
  },
  QUICKBOOKS_QUERY_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "QuickBooks query failed",
  },
  QUICKBOOKS_CDC_DELETION_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Failed to process QuickBooks CDC deletion payload",
  },
  RFQ_PROJECTS_ERROR: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Error getting projects for request for quote",
  },
  PARTNER_CUSTOMER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Could not find customer for partner invoice",
  },
  INVOICE_VOID_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Failed to void invoice in QuickBooks",
  },
  INVOICE_CREATION_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Failed to create invoice in QuickBooks",
  },
  INVOICE_CLOSED_CANNOT_VOID: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot void closed invoice",
  },
  INVOICE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Invoice not found",
  },
  ACTIVE_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active quote found for project",
  },
  ESCROW_MISSING_QUOTE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot create escrow without an active quote",
  },
  ESCROW_SUB_DOLLAR_RESIDUAL_ON_FUNDED_ESCROW: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Sub-dollar escrow residual on an already-funded escrow; refusing to auto-cancel funded money",
  },
  ESCROW_CONTRACT_BELOW_COMMITTED_ESCROW: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Agreement would size escrow below money already committed to closed escrows; refunds must be handled in PayKeeper",
  },
  ESCROW_UPSERT_LOCK_CONTENDED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Another escrow upsert for this RFQ held the lock past the wait budget",
  },
  ESCROW_RELEASED_FUNDS_EXCEED_QUOTE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote drops the escrow below the minimum after its funds were already released",
  },
  ESCROW_ALREADY_TERMINAL: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Refused to act on an escrow already in a terminal status (cancelled or fully disbursed)",
  },
  ESCROW_CANCELLED_WITHOUT_LOCAL_INTENT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "PayKeeper reported an escrow cancellation that Remi never initiated; remediation was suppressed",
  },
  FINANCING_MISSING_HIC_FILE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No HIC file found for financing document upload",
  },
  FINANCING_MISSING_REQUEST_FOR_QUOTE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No RFQ found for financing document upload",
  },
  FINANCING_MISSING_ACTIVE_QUOTE_FOR_MILESTONE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "No active quote found when attempting to claim financing milestone",
  },
  FINANCING_MISSING_LOAN_AMOUNT: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to get loan amount from financing partner",
  },
  FINANCING_MISSING_PROJECT_COST: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Failed to get project cost from request for quote",
  },
  FUNDING_NO_REQUESTS_FOR_QUOTE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No requests for quote found for bid",
  },
  FUNDING_NO_BID_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No bid found for funding completion",
  },
  FUNDING_NO_MILESTONE_FOR_CATEGORY: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No funding milestone defined for project category",
  },
  FUNDING_MISSING_ACTIVE_QUOTE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active quote found for funding update",
  },
  FUNDING_DUPLICATE_CANCELLATION_MILESTONE: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Request for quote already has a cancellation milestone",
  },
  FUNDING_EXISTING_CANCELLATION_FEE: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Escrow or financing already has a cancellation fee",
  },
  FUNDING_MISSING_ACTIVE_QUOTE_FOR_CANCELLATION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "No active quote found for funding cancellation",
  },
  FUNDING_CANCELLATION_FEE_EXCEEDS_FINANCING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Financing cancellation fee exceeds the financing amount",
  },
  FINANCING_EXTERNAL_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Financing with external ID does not exist",
  },
  FINANCING_REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found for financing",
  },
  FINANCING_EXTERNAL_ID_ALREADY_EXISTS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Financing with given external ID already exists for this vendor",
  },
  FINANCING_CREATION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Unable to create financing from external ID",
  },
  FINANCING_CLAIM_RECOVERY_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Financing claim hit P2002 but the winning row was not found",
  },
  FINANCING_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Financing not found",
  },
  FINANCING_INVALID_STATUS_FOR_UPDATE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Financing status is in a declined, canceled, or funded state",
  },
  FINANCING_INVALID_STATUS_FOR_ZERO_AMOUNT_CANCEL: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Refused to locally cancel a $0 financing already past loan-document signing or partially/fully disbursed",
  },
  INVALID_STIPULATION_DOCUMENT_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Stipulation document type does not match any stored folderName",
  },
  FINANCING_STIPULATIONS_MALFORMED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Stored Financing.stipulations JSON failed schema validation",
  },
  STIPULATION_UPLOAD_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "One or more stipulation file uploads to Sunlight failed",
  },
  CALL_STIPULATIONS_NOT_APPROVED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Stipulation submission blocked while a phone-call stip is not yet approved",
  },
  CASH_CHECK_ALREADY_EXISTS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Active cash/check payment already exists for this RFQ",
  },
  CASH_CHECK_CUSTOMER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Cash/check payment QB parent customer not found",
  },
  CASH_CHECK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Cash/check payment not found",
  },
  ESCROW_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Escrow not found",
  },
  INVOICE_REMI_ENTITY_MISMATCH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Project invoices have remi entity mismatch",
  },
  INVOICE_REMI_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "@support No REMI-id found for manually created invoice",
  },
  INVOICE_VOID_BATCH_ERROR: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Error voiding invoices",
  },
  CREDIT_MEMO_DELETE_BATCH_ERROR: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Error deleting credit memos",
  },
  QB_INVOICE_FETCH_BATCH_ERROR: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Error getting QB invoices from DB invoices",
  },
  QB_CREDIT_MEMO_FETCH_BATCH_ERROR: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Error getting QB credit memos from DB credit memos",
  },
  JOURNAL_ENTRY_PROJECT_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No project id found for journal entry",
  },
  CREDIT_MEMO_REMI_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "@support No REMI-id found for manually created credit memo",
  },
  VENDOR_CREDIT_REMI_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "@support No REMI-id found for manually created vendor credit",
  },
  VENDOR_CREDIT_VENDOR_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Vendor not found for vendor credit",
  },
  VENDOR_CREDIT_PAYEE_NOT_IN_QUICKBOOKS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "This payee isn't set up as a vendor in QuickBooks, so the vendor credit can't be created",
  },
  VENDOR_CREDIT_SUPPLIER_PAYEE_REQUIRES_MATERIALS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Vendor credits to a materials supplier must use the Materials type",
  },
  FINANCING_SUBMISSION_ERROR: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Error submitting financing or sending loan docs",
  },
  BILL_REMI_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "@support No REMI-id found for manually created bill",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  INVALID_SELECTED_NET_TERMS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid selected net terms",
  },
  MISMATCHED_BIDS_FOR_MOVE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Source and target projects do not share the same bid for bill move",
  },
  INVOICE_BID_REQUEST_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No bid request ID found for invoice generation",
  },
  BID_REQUEST_EXTERNAL_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No external ID found for bid request or bid approval",
  },
  ESCROW_NO_FUNDS_AVAILABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "No funds available in escrow for this project",
  },
  PAYKEEPER_SCHEDULE_READ_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to read the PayKeeper milestone schedule for an escrow",
  },
  CONTRACT_PARTY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contract party not found in bid data for project",
  },
  NEGATIVE_INVOICE_PAYLOAD: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invoice payload has negative total amount with no matching QB items for catch-up",
  },
  ORPHANED_CUSTOMER_OVER_CREDITED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Credit memos exceed the immutable invoices they offset for an orphaned customer",
  },
  BID_NOT_FOUND_FOR_QB_ENTITY: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No bid found for project when resolving QuickBooks entity",
  },
  ESCROW_HOMEOWNER_MISSING_CONTACT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Homeowner is missing email needed for partner-flow escrow create",
  },
  ESCROW_CONTRACTOR_ORG_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Escrow's bid project has no CONTRACTOR organization for flag scoping",
  },
  FUNDING_USER_MISSING_EMAIL: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Authenticated user has no primary email — required for PayKeeper audit attribution",
  },
  STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Sunlight catalog does not contain the required Strategic Partner loan product (180-month, 6.99% APR, HII, ACH) or it carries unexpected fee/promo fields",
  },
  FINANCING_QUOTE_AREA_ZERO: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Quote has zero roof area — skipping GoodLeap project info upload",
  },
  JOURNAL_ENTRY_OPERATION_IN_PROGRESS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Journal entry operation already in progress for this project",
  },
  BILLS_PENDING_REVIEW: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Bill creation/payment blocked - review-needed bills still pending approval",
  },
  WORK_ORDER_ONLY_INVOICE_REPORT_UNREADABLE_SUBMISSION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Completed WO-only invoice automation payload did not parse, so its work order is off the FS Billing sheet",
  },
  WORK_ORDER_ONLY_INVOICE_REPORT_WORK_ORDER_NUMBER_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "WO-only work order external id is not numeric, so Sunrun AP cannot match the invoice",
  },
  WORK_ORDER_ONLY_INVOICE_REPORT_WINDOW_INCOMPLETE: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription:
      "WO-only invoice rows failed to assemble, so the sheet was withheld rather than billing part of the window",
  },
  WORK_ORDER_ONLY_INVOICE_REPORT_SEND_UNCONFIRMED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "A claimed WO-only invoice sheet never confirmed delivery — resolve manually before the report resumes",
  },
  COC_INVOICE_SOURCE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription:
      "No bid request with an active quote for the partner, so the Certificate of Completion invoice cannot be built",
  },
  COC_INVOICE_MULTIPLE_ACTIVE_QUOTES: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "The partner's bid requests span more than one active quote, so one Certificate of Completion invoice cannot cover them",
  },
  COC_INVOICE_ESTIMATE_MISSING: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "The active quote has no estimate, so the Certificate of Completion invoice has no amounts",
  },
  COC_INVOICE_AMOUNT_INVALID: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "The estimate total is not positive or the deductible exceeds it, so the Certificate of Completion invoice is held",
  },
  COC_INVOICE_BILL_TO_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "No invoice bill-to is configured for this partner flow, so its Certificate of Completion is held",
  },
  COC_INVOICE_VERSION_CONFLICT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A concurrent send created a Certificate of Completion invoice with different amounts; retry",
  },
};
